import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import SettingsModal from "../dashboard/SettingsModal";
import "./../../assets/css/nav.css";

const LANGUAGE_META = {
    0: { name: "German", flag: "de.svg" },
    1: { name: "Mexican Spanish", flag: "mx.svg" },
    2: { name: "French", flag: "fr.svg" },
    3: { name: "Russian", flag: "ru.svg" },
    4: { name: "Brazilian Portuguese", flag: "br.svg" }
};

function TopNav({ onSettingsClick, registeredCourses = [], activeLanguageId = null, onLanguageChange }) {
    const navigate = useNavigate();
    const { isAuthenticated, logout, user } = useAuth();
    const [menuOpen, setMenuOpen] = useState(false);
    const [showMobileDropdown, setShowMobileDropdown] = useState(false);
    const [localSettingsOpen, setLocalSettingsOpen] = useState(false);

    const hasSidebar = Boolean(onSettingsClick);

    const goHome = () => {
        setMenuOpen(false);
        navigate("/");
    };

    const handleAboutClick = (e) => {
        const aboutEl = document.getElementById("about-section");
        if (aboutEl) {
            e.preventDefault();
            aboutEl.scrollIntoView({ behavior: "smooth" });
        } else {
            e.preventDefault();
            navigate("/#about-section");
        }
    };

    const handleSettingsAction = () => {
        if (onSettingsClick) {
            onSettingsClick();
        } else {
            setLocalSettingsOpen(true);
        }
    };

    const closeMenu = useCallback(() => setMenuOpen(false), []);

    /* Close mobile menu on resize to desktop */
    useEffect(() => {
        const onResize = () => { if (window.innerWidth > 768) setMenuOpen(false); };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    /* Toggle body class so SideNav can respond via CSS */
    useEffect(() => {
        if (menuOpen) {
            document.body.classList.add("mobile-nav-open");
        } else {
            document.body.classList.remove("mobile-nav-open");
        }
        return () => document.body.classList.remove("mobile-nav-open");
    }, [menuOpen]);

    const activeMeta = LANGUAGE_META[activeLanguageId] || { name: "", flag: "" };

    return (
        <>
            <header className={`top-nav${hasSidebar ? "" : " top-nav--standalone"}`}>
                <div className="top-nav__left">
                    {/* Hamburger - visible only on mobile when SideNav is present */}
                    {hasSidebar && (
                        <button
                            type="button"
                            className="hamburger-btn"
                            onClick={() => setMenuOpen((v) => !v)}
                            aria-label={menuOpen ? "Close menu" : "Open menu"}
                            aria-expanded={menuOpen}
                        >
                            {menuOpen ? "✕" : "☰"}
                        </button>
                    )}
                    <button type="button" className="top-nav__logo" onClick={goHome} aria-label="FreeFlash home">
                        <img src="/logo.png" alt="FreeFlash logo" className="top-nav__logo-mark" />
                        <span className="top-nav__logo-text">FreeFlash</span>
                    </button>
                </div>
                <nav className="top-nav__center">
                    <a href="/#about-section" className="top-nav__link" onClick={handleAboutClick}>About</a>
                    <a
                        href="https://docs.google.com/forms/d/e/1FAIpQLSf0aqg6AYb0n5LW75ons25nzfngrjHZMu06KlfRRDf8cT2x1w/viewform?usp=dialog"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="top-nav__link"
                    >
                        Feedback
                    </a>
                </nav>

                {/* Mobile Language Switcher */}
                {registeredCourses.length > 0 && activeLanguageId !== null && activeMeta.flag && (
                    <div className="top-nav__mobile-lang-container" style={{ position: "relative" }}>
                        <button
                            type="button"
                            className="top-nav__mobile-lang-btn"
                            onClick={() => setShowMobileDropdown(!showMobileDropdown)}
                            aria-haspopup="listbox"
                            aria-expanded={showMobileDropdown}
                        >
                            <img
                                src={require(`../../assets/images/${activeMeta.flag}`)}
                                alt={activeMeta.name}
                                className="top-nav__mobile-flag"
                            />
                            <span className="top-nav__mobile-lang-arrow">▼</span>
                        </button>
                        {showMobileDropdown && (
                            <div className="top-nav__mobile-dropdown">
                                <ul className="top-nav__mobile-dropdown-list" role="listbox">
                                    {registeredCourses.map((course) => {
                                        const langId = parseInt(course.language_id, 10);
                                        if (langId === activeLanguageId) return null;
                                        const meta = LANGUAGE_META[langId];
                                        return (
                                            <li key={course.id}>
                                                <button
                                                    type="button"
                                                    className="top-nav__mobile-dropdown-item"
                                                    onClick={() => {
                                                        onLanguageChange?.(langId);
                                                        setShowMobileDropdown(false);
                                                    }}
                                                    role="option"
                                                    aria-selected={false}
                                                >
                                                    {meta?.flag && (
                                                        <img
                                                            src={require(`../../assets/images/${meta.flag}`)}
                                                            alt=""
                                                            className="top-nav__mobile-dropdown-flag"
                                                        />
                                                    )}
                                                    <span style={{ textTransform: "capitalize" }}>{meta?.name || course.language}</span>
                                                </button>
                                            </li>
                                        );
                                    })}
                                    {registeredCourses.length <= 1 && (
                                        <li className="top-nav__mobile-dropdown-empty">
                                            No other courses.
                                        </li>
                                    )}
                                </ul>
                            </div>
                        )}
                    </div>
                )}

                <div className="top-nav__right">
                    {isAuthenticated ? (
                        <>
                            <button type="button" className="top-nav__action" onClick={handleSettingsAction}>
                                Account settings
                            </button>
                            <button type="button" className="top-nav__action" onClick={logout}>
                                Sign Out
                            </button>
                        </>
                    ) : (
                        <button type="button" className="top-nav__action" onClick={() => navigate("/login")}>
                            Log In
                        </button>
                    )}
                </div>
            </header>

            {/* Mobile menu backdrop - rendered when menuOpen */}
            {menuOpen && (
                <div className="mobile-backdrop" onClick={closeMenu} aria-hidden="true" />
            )}

            {/* Standalone SettingsModal when TopNav is used outside LearningDashboard */}
            {!onSettingsClick && isAuthenticated && (
                <SettingsModal
                    isOpen={localSettingsOpen}
                    onClose={() => setLocalSettingsOpen(false)}
                    userId={user?.id}
                />
            )}
        </>
    );
}

export default TopNav;
